import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import type { AnalysisData } from './analysisTypes';
import type { DirectShearStage } from '../../../types/directShear';

interface SummaryResultsTableProps {
  analysisByStage: Record<number, AnalysisData>;
  stages?: DirectShearStage[];
}

const fmt = (v: number | null | undefined, d = 2): string =>
  v != null ? v.toFixed(d) : '\u2014';

const cellSx = { fontSize: '0.78rem', py: 0.5, px: 1 };
const headerSx = { ...cellSx, fontWeight: 700 };

const ROWS: { label: string; unit: string; field: keyof AnalysisData; decimals?: number }[] = [
  { label: 'Normal Stress (\u03C3n)', unit: 'kPa', field: 'peakNormalStress' },
  { label: 'Peak Shear Stress (\u03C4p)', unit: 'kPa', field: 'peakShearStress' },
  { label: 'Residual Shear Stress (\u03C4r)', unit: 'kPa', field: 'residualShearStress' },
  { label: 'Peak Shear Displacement', unit: 'mm', field: 'peakHorizontalDisp', decimals: 3 },
  { label: 'Residual Shear Displacement', unit: 'mm', field: 'residualHorizontalDisp', decimals: 3 },
  { label: 'Peak Friction Angle (\u03C6p)', unit: '\u00B0', field: 'peakFrictionAngle' },
  { label: 'Residual Friction Angle (\u03C6r)', unit: '\u00B0', field: 'residualFrictionAngle' },
];

const STAGES = [1, 2, 3, 4, 5];

export const SummaryResultsTable = ({ analysisByStage, stages }: SummaryResultsTableProps) => {
  const stageArea = (s: number): number | null => {
    const st = stages?.find((st) => st.stageNumber === s);
    return st?.area ?? null;
  };

  return (
    <TableContainer sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5, fontSize: '0.85rem', fontWeight: 700 }}>
        Peak &amp; Residual Summary
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={headerSx}>Parameter</TableCell>
            <TableCell sx={headerSx}>Unit</TableCell>
            {STAGES.map((s) => (
              <TableCell key={s} sx={headerSx} align="right">S{s}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Range display rows — show which stages have ranges configured */}
          <TableRow>
            <TableCell sx={{ ...cellSx, fontWeight: 600, color: 'primary.main' }}>Peak Range</TableCell>
            <TableCell sx={cellSx}>mm</TableCell>
            {STAGES.map((s) => {
              const a = analysisByStage[s];
              const from = a?.peakRangeFrom;
              const to = a?.peakRangeTo;
              return (
                <TableCell key={s} sx={cellSx} align="right">
                  {from != null && to != null ? `${from}\u2013${to}` : '\u2014'}
                </TableCell>
              );
            })}
          </TableRow>
          <TableRow>
            <TableCell sx={{ ...cellSx, fontWeight: 600, color: 'error.main' }}>Residual Range</TableCell>
            <TableCell sx={cellSx}>mm</TableCell>
            {STAGES.map((s) => {
              const a = analysisByStage[s];
              const from = a?.residualRangeFrom;
              const to = a?.residualRangeTo;
              return (
                <TableCell key={s} sx={cellSx} align="right">
                  {from != null && to != null ? `${from}\u2013${to}` : '\u2014'}
                </TableCell>
              );
            })}
          </TableRow>
          {/* Area row — from stages data */}
          <TableRow>
            <TableCell sx={cellSx}>Area</TableCell>
            <TableCell sx={cellSx}>cm²</TableCell>
            {STAGES.map((s) => {
              const area = stageArea(s);
              return (
                <TableCell key={s} sx={cellSx} align="right">
                  {area != null ? (area * 10000).toFixed(2) : '\u2014'}
                </TableCell>
              );
            })}
          </TableRow>
          {/* Analysis rows */}
          {ROWS.map((row) => (
            <TableRow key={row.label}>
              <TableCell sx={cellSx}>{row.label}</TableCell>
              <TableCell sx={cellSx}>{row.unit}</TableCell>
              {STAGES.map((s) => {
                const a = analysisByStage[s];
                const val = a ? (a[row.field] as number | null) : null;
                return (
                  <TableCell key={s} sx={cellSx} align="right">
                    {fmt(val, row.decimals)}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
